import { memo, useEffect, useRef, useState } from "react";
import { ChatStore } from "@/stores/ChatStore";
import isFullSentence from "@/lib/isFullSentence";
import AvatarDisplay from "./avatar-display";
import useAvatarSession from "@/hooks/useAvatarSession";

function InteractiveAvatar() {
  const deltaBuffer = ChatStore(state => state.deltaBuffer);
  const isRunning = ChatStore(state => state.isRunning);
  const isForcedToInterrupt = ChatStore(state => state.isForcedToInterrupt);
  const setIsForcedToInterrupt = ChatStore(state => state.setIsForcedToInterrupt);
  const responseSessionId = ChatStore(state => state.responseSessionId);

  const { isStreamReady, isLoadingSession, startSession, endSession, handleSpeak, interruptAvatar, selectedAvatar, attachVideo } = useAvatarSession();

  const sessionStarted = useRef(false);
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [isProcessingSentence, setIsProcessingSentence] = useState(false);
  const processingSentenceIndex = useRef<number | null>(null);
  const frozenSentences = useRef<string[]>([]);

  useEffect(() => {
    if (isRunning) {
      startSession();
      sessionStarted.current = true;
    }
    return () => {
      if (sessionStarted.current) {
        endSession();
      }
    };
  }, [isRunning, startSession, endSession]);

  useEffect(() => {
    if (isRunning && isForcedToInterrupt) {
      interruptAvatar();
      setCurrentSentenceIndex(0);
      setIsForcedToInterrupt(false);
      processingSentenceIndex.current = null;
      frozenSentences.current = [];
    }
  }, [interruptAvatar, isForcedToInterrupt]);

  useEffect(() => {
    if (responseSessionId > 0) {
      setCurrentSentenceIndex(0);
      setIsProcessingSentence(false);
      processingSentenceIndex.current = null;
      frozenSentences.current = [];
    }
  }, [responseSessionId]);

  useEffect(() => {
    if (isProcessingSentence || processingSentenceIndex.current === currentSentenceIndex) return;

    const sentenceToSay = frozenSentences.current[currentSentenceIndex] || deltaBuffer[currentSentenceIndex];

    if (isFullSentence(sentenceToSay)) {
      frozenSentences.current[currentSentenceIndex] = sentenceToSay;

      setIsProcessingSentence(true);
      processingSentenceIndex.current = currentSentenceIndex;

      handleSpeak(sentenceToSay).finally(() => {
        setIsProcessingSentence(false);
        processingSentenceIndex.current = null;
        setCurrentSentenceIndex(prev => prev + 1);
      });
    }
  }, [deltaBuffer.length, currentSentenceIndex, handleSpeak]);

  return (
    <AvatarDisplay
      isStreamReady={isStreamReady}
      isLoadingSession={isLoadingSession}
      selectedAvatar={selectedAvatar}
      attachVideo={attachVideo}
    />
  );
}

export default memo(InteractiveAvatar);
